/**
 * Documents attached to one parent, with download and delete.
 *
 * Downloads go through `GET /api/documents/:id/download`, which authorizes the read
 * through RLS and only then mints a short-lived presigned URL. The URL is never
 * stored or rendered into an `<img src>` on a list — it is a bearer capability with
 * a few minutes of life, and putting one in the DOM for every row would mint
 * dozens of them per page load and leave them in the browser cache.
 *
 * So thumbnails are deliberately absent. A filename and a type is enough for a
 * console, and "click to open" costs one request at the moment of intent.
 */

import { useState } from 'react';
import { useInvalidate, useList } from '@refinedev/core';
import { api } from '../lib/api.js';
import { DataTable, type Column } from './DataTable.js';
import { formatBytes, documentTypeLabel } from './PhotoUpload.js';

export interface DocumentRow {
  id: string;
  documentType: string;
  visibility: string;
  fileName: string;
  sizeBytes: number;
  description: string | null;
  uploadedAt: string | null;
}

interface DocumentListProps {
  /**
   * Which parent to list under. OMIT IT to list everything in scope — the endpoint
   * takes every filter as optional, so an unfiltered call returns exactly the
   * documents the caller's policies permit. That is what the portals use: a tenant's
   * "Documents" page is this component with no filter at all, and it is correct
   * because `documents_tenant_select` already decided the answer.
   */
  filter?:
    | { propertyId: string }
    | { unitId: string }
    | { leaseId: string }
    | { tenantId: string }
    | { workOrderId: string }
    | { ownerId: string }
    | { applicationId: string };
  /** Staff may delete; portal actors have no policy to, so the column is hidden. */
  canDelete?: boolean;
  emptyMessage?: string;
}

export function DocumentList({ filter, canDelete = false, emptyMessage }: DocumentListProps) {
  const invalidate = useInvalidate();
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const entry = filter ? (Object.entries(filter)[0] as [string, string]) : null;

  const {
    data,
    isLoading,
    error: listError,
  } = useList<DocumentRow>({
    resource: 'documents',
    filters: entry ? [{ field: entry[0], operator: 'eq', value: entry[1] }] : [],
    // The API caps this list at 200 and does not paginate it. A single property with
    // more than 200 documents is a different feature request, not a pager.
    pagination: { mode: 'off' },
  });

  async function download(row: DocumentRow) {
    setBusyId(row.id);
    setError(null);
    try {
      const { downloadUrl } = await api<{ downloadUrl: string }>(`/documents/${row.id}/download`);
      // Opened rather than fetched. The presigned URL responds with
      // Content-Disposition: attachment, so the browser saves it without us ever
      // holding the bytes.
      window.open(downloadUrl, '_blank', 'noopener,noreferrer');
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not open that file.');
    } finally {
      setBusyId(null);
    }
  }

  async function remove(row: DocumentRow) {
    if (!window.confirm(`Delete ${row.fileName}? This removes the stored file too.`)) return;

    setBusyId(row.id);
    setError(null);
    try {
      await api<void>(`/documents/${row.id}`, { method: 'DELETE' });
      await invalidate({ resource: 'documents', invalidates: ['list'] });
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not delete that file.');
    } finally {
      setBusyId(null);
    }
  }

  const columns: Column<DocumentRow>[] = [
    {
      key: 'fileName',
      header: 'File',
      render: (row) => (
        <>
          <strong>{row.fileName}</strong>
          {row.description ? <div className="muted">{row.description}</div> : null}
        </>
      ),
    },
    { key: 'type', header: 'Type', render: (row) => documentTypeLabel(row.documentType) },
    {
      key: 'visibility',
      header: 'Visible to',
      render: (row) => <span className="pill">{documentTypeLabel(row.visibility)}</span>,
    },
    { key: 'size', header: 'Size', numeric: true, render: (row) => formatBytes(row.sizeBytes) },
    {
      key: 'actions',
      header: '',
      render: (row) => (
        <div className="row">
          <button
            type="button"
            className="button button--quiet"
            disabled={busyId === row.id}
            onClick={() => void download(row)}
          >
            Open
          </button>
          {canDelete ? (
            <button
              type="button"
              className="button button--quiet"
              disabled={busyId === row.id}
              onClick={() => void remove(row)}
            >
              Delete
            </button>
          ) : null}
        </div>
      ),
    },
  ];

  return (
    <>
      {error ? <p className="alert alert--error">{error}</p> : null}
      <DataTable
        columns={columns}
        rows={data?.data ?? []}
        rowKey={(row) => row.id}
        isLoading={isLoading}
        error={listError}
        emptyMessage={emptyMessage ?? 'No files yet.'}
      />
    </>
  );
}
